import {
    Button,
    styled,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    CircularProgress
} from "@mui/material";
import React, {useState, useEffect} from "react";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';
import {path} from "../../../routes";

const EditOrg_modal = (props) => {
    const VisuallyHiddenInput = styled('input')({
        clip: 'rect(0 0 0 0)',
        clipPath: 'inset(50%)',
        height: 1,
        overflow: 'hidden',
        position: 'absolute',
        bottom: 0,
        left: 0,
        whiteSpace: 'nowrap',
        width: 1,
    });

    const [file, setFile] = useState(null);
    const [text, setText] = useState(props.organization?.name || '');
    const [imageUrl, setImageUrl] = useState('');
    const [imageKey, setImageKey] = useState(Date.now());
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (props.organization) {
            setText(props.organization.name || '');
            // Pokud organizace má logo, zobrazíme ho s cache-busting
            if (props.organization.logoPath) {
                const baseUrl = props.organization.logoPath.startsWith('http') 
                    ? props.organization.logoPath 
                    : `${path}/${props.organization.logoPath}`;
                // Přidáme timestamp pro cache-busting při každém otevření modalu
                const logoUrl = `${baseUrl}?t=${Date.now()}`;
                setImageUrl(logoUrl);
                setImageKey(Date.now());
            } else {
                setImageUrl('');
            }
        }
    }, [props.organization]);

    const handleTextChange = (event) => {
        setText(event.target.value);
    };

    const close_handler = () => {
        props.close();
    };

    const handleFileChange = (event) => {
        const selectedFile = event.target.files[0];
        setFile(selectedFile);
        if (selectedFile) {
            const reader = new FileReader();
            reader.onload = (e) => {
                // Pro preview používáme data URL
                setImageUrl(e.target.result);
                // Aktualizujeme key pro force refresh
                setImageKey(Date.now());
            };
            reader.readAsDataURL(selectedFile);
        }
    };

    const delete_file_handler = () => {
        setFile(null);
        if (props.organization?.logoPath) {
            const baseUrl = props.organization.logoPath.startsWith('http') 
                ? props.organization.logoPath 
                : `${path}/${props.organization.logoPath}`;
            const logoUrl = `${baseUrl}?t=${Date.now()}`;
            setImageUrl(logoUrl);
            setImageKey(Date.now());
        } else {
            setImageUrl('');
        }
    };

    const handleSubmit = async () => {
        if (!text.trim()) {
            alert('Please enter an organization name');
            return;
        }

        setLoading(true);
        try {
            const formData = new FormData();
            formData.append('description', text);
            
            // Pokud je vybrán nový soubor, přidáme ho
            if (file) {
                formData.append('file', file);
            }

            const response = await axios.put(`${path}/updateOrganization/${props.organization.id}`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            // Aktualizujeme imageUrl s novou cestou z backendu
            if (response.data && response.data.logoPath) {
                const baseUrl = response.data.logoPath.startsWith('http') 
                    ? response.data.logoPath 
                    : `${path}/${response.data.logoPath}`;
                // Přidáme timestamp pro cache-busting, aby prohlížeč načetl nový obrázek
                const timestamp = Date.now();
                const newLogoUrl = `${baseUrl}?t=${timestamp}`;
                
                // Vyčistíme vybraný soubor PŘED nastavením nové URL
                setFile(null);
                
                // Počkáme chvilku, aby React aktualizoval DOM
                await new Promise(resolve => setTimeout(resolve, 100));
                
                // Teď nastavíme nové logo
                setImageUrl(newLogoUrl);
                setImageKey(timestamp);
                
                console.log('Logo updated:', newLogoUrl);
            }

            alert('Organization updated successfully');
            
            // Počkáme chvilku, aby uživatel viděl aktualizovaný obrázek před zavřením
            setTimeout(() => {
                props.close();
            }, 500);
        } catch (error) {
            console.error('Error updating organization:', error);
            alert('Failed to update organization');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog 
            open={true} 
            onClose={close_handler}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: { xs: 0, sm: '12px' },
                    maxHeight: { xs: '100vh', sm: '90vh' },
                    margin: { xs: 0, sm: '32px' },
                    width: { xs: '100%', sm: 'auto' }
                }
            }}
        >
            <DialogTitle sx={{ 
                fontSize: { xs: '18px', sm: '20px' },
                fontWeight: 600,
                pb: 1.5,
                borderBottom: '1px solid #e0e0e0'
            }}>
                Edit organization
            </DialogTitle>
            
            <DialogContent sx={{ 
                pt: 3,
                pb: 3,
                px: { xs: 2, sm: 3 }
            }}>
                {/* Current logo */}
                {imageUrl && !file && (
                    <Box sx={{ 
                        mb: 3,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 1,
                        paddingTop: 2,
                    }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '14px' }}>
                            Current logo:
                        </Typography>
                        <Box sx={{
                            maxWidth: { xs: '100%', sm: '250px' },
                            maxHeight: { xs: '150px', sm: '200px' },
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            border: '1px solid #e0e0e0',
                            borderRadius: '8px',
                            padding: 1,
                            backgroundColor: '#f8f9fa'
                        }}>
                            <img 
                                key={imageKey}
                                src={imageUrl} 
                                alt="Current logo" 
                                style={{ 
                                    maxWidth: '100%',
                                    maxHeight: '100%',
                                    objectFit: 'contain',
                                    borderRadius: '4px'
                                }}
                                onError={(e) => {
                                    console.error('Error loading logo:', imageUrl);
                                    e.target.style.opacity = '0.5';
                                }}
                            />
                        </Box>
                    </Box>
                )}

                {/* Upload new logo / preview */}
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 2,
                    mb: 3
                }}>
                    {file === null ? (
                        <Button
                            color="primary"
                            component="label"
                            role={undefined}
                            variant="contained"
                            tabIndex={-1}
                            startIcon={<CloudUploadIcon/>}
                            sx={{ 
                                width: { xs: '100%', sm: 'auto' },
                                minWidth: { xs: '100%', sm: 200 },
                                height: { xs: '48px', sm: 'auto' },
                                fontSize: '15px',
                                fontWeight: 500
                            }}
                        >
                            Upload new logo
                            <VisuallyHiddenInput type="file" accept="image/*" onChange={handleFileChange}/>
                        </Button>
                    ) : (
                        <Box sx={{
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 2
                        }}>
                            <Box sx={{
                                maxWidth: { xs: '100%', sm: '250px' },
                                maxHeight: { xs: '150px', sm: '200px' },
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                border: '1px solid #e0e0e0',
                                borderRadius: '8px',
                                padding: 1,
                                backgroundColor: '#f8f9fa',
                                width: '100%'
                            }}>
                                <img 
                                    key={imageKey}
                                    src={imageUrl} 
                                    alt="New logo preview" 
                                    style={{ 
                                        maxWidth: '100%',
                                        maxHeight: '100%',
                                        objectFit: 'contain',
                                        borderRadius: '4px'
                                    }}
                                    onError={(e) => {
                                        console.error('Error loading preview:', imageUrl);
                                    }}
                                />
                            </Box>
                            <Button 
                                variant="outlined" 
                                color="error" 
                                onClick={delete_file_handler} 
                                startIcon={<DeleteIcon/>}
                                sx={{ 
                                    width: { xs: '100%', sm: 'auto' },
                                    minWidth: { xs: '100%', sm: 200 }
                                }}
                            >
                                Remove new image
                            </Button>
                        </Box>
                    )}
                </Box>

                {/* Organization name */}
                <TextField 
                    sx={{ 
                        width: '100%',
                        mt: { xs: 1, sm: 0 }
                    }} 
                    id="outlined-basic" 
                    label="Organization name" 
                    variant="outlined" 
                    value={text}
                    onChange={handleTextChange}
                    fullWidth
                    required
                />
            </DialogContent>
            
            <DialogActions sx={{ 
                px: { xs: 2, sm: 3 },
                pb: { xs: 2, sm: 2.5 },
                pt: 2,
                borderTop: '1px solid #e0e0e0',
                gap: { xs: 1, sm: 1.5 },
                flexDirection: { xs: 'column', sm: 'row' }
            }}>
                <Button 
                    onClick={close_handler} 
                    disabled={loading}
                    variant="outlined"
                    sx={{
                        width: { xs: '100%', sm: 'auto' },
                        minWidth: { xs: '100%', sm: 120 },
                        height: { xs: '48px', sm: 'auto' },
                        order: { xs: 2, sm: 1 }
                    }}
                >
                    Cancel
                </Button>
                <Button 
                    disabled={!text.trim() || loading} 
                    onClick={handleSubmit}
                    variant="contained"
                    sx={{
                        width: { xs: '100%', sm: 'auto' },
                        minWidth: { xs: '100%', sm: 120 },
                        height: { xs: '48px', sm: 'auto' },
                        fontWeight: 600,
                        order: { xs: 1, sm: 2 },
                        boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                        '&:hover': {
                            boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)'
                        },
                        '&:disabled': {
                            backgroundColor: '#e0e0e0',
                            color: '#9e9e9e'
                        }
                    }}
                >
                    {loading ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CircularProgress size={20} color="inherit" />
                            <span>Saving...</span>
                        </Box>
                    ) : (
                        'Save'
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditOrg_modal;
